import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment } from 'react';
import { EnumMap } from '../../../types';
import { safeEnumMapGet } from '../../../utils/EnumHelper';
import { cn } from '../../../utils/classUtils';

export enum StepVariant {
  Default = 'default',
}

interface StepV2Props {
  isInProgress: boolean;
  isCompleted: boolean;
  isFinal: boolean;
  inProgressPercentage?: number;
  isVertical?: boolean;
  variant?: StepVariant;
  size?: number;
  strokeWidth?: number;
  strokeColor?: string;
}

export const variantToCompletedColorMap: EnumMap<StepVariant, string> = {
  [StepVariant.Default]: 'text-reskin-primary-blue',
};

export const variantToCompletedBorderColorMap: EnumMap<StepVariant, string> = {
  [StepVariant.Default]: 'border-reskin-primary-blue',
};

export const variantToColorMap: EnumMap<StepVariant, string> = {
  [StepVariant.Default]: 'text-reskin-grey-200',
};

export const variantToBorderColorMap: EnumMap<StepVariant, string> = {
  [StepVariant.Default]: 'border-reskin-grey-200',
};

const StepV2: React.FC<StepV2Props> = ({
  isInProgress,
  isCompleted,
  isFinal,
  inProgressPercentage,
  isVertical = false,
  variant = StepVariant.Default,
  size = 36,
  strokeWidth = 6,
  strokeColor = '#4967FD',
}) => {
  const stepCompletedColor = safeEnumMapGet(
    variantToCompletedColorMap,
    variant,
    StepVariant.Default,
  );

  const stepCompletedBorderColor = safeEnumMapGet(
    variantToCompletedBorderColorMap,
    variant,
    StepVariant.Default,
  );

  const stepColor = safeEnumMapGet(
    variantToColorMap,
    variant,
    StepVariant.Default,
  );

  const stepBorderColor = safeEnumMapGet(
    variantToBorderColorMap,
    variant,
    StepVariant.Default,
  );

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - (inProgressPercentage || 0) / 100);

  return (
    <Fragment>
      <div
        className={cn('flex flex-row items-center justify-center', {
          'mx-1': variant === StepVariant.Default,
        })}
      >
        {isCompleted ? (
          <FontAwesomeIcon
            icon={faCircleCheck}
            fontSize={36}
            className={stepCompletedColor}
          />
        ) : isInProgress ? (
          <div style={{ position: 'relative', width: size, height: size }}>
            <FontAwesomeIcon
              icon={faCircle}
              style={{
                fontSize: size,
                color: '#eee',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
            <svg
              width={size}
              height={size}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                transform: 'rotate(90deg) scaleX(-1)',
              }}
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill='none'
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap='round'
              />
            </svg>
          </div>
        ) : (
          <FontAwesomeIcon
            icon={faCircle}
            fontSize={36}
            className={cn(isInProgress ? stepCompletedColor : stepColor)}
          />
        )}
      </div>
      <div className='flex flex-1 relative'>
        {!isFinal && (
          <div
            className={cn(
              'flex flex-1 items-center',
              isVertical ? 'flex-col' : 'flex-row',
            )}
          >
            <div
              className={cn(
                'flex',
                'flex-1',
                'border-[1.5px]',
                isCompleted ? stepCompletedBorderColor : stepBorderColor,
              )}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default StepV2;
